import * as React from 'react';
import { ConsultationDto } from 'ts-types/api.types';
import { BillStatus } from 'ts-types/api.enums';
import axios, { CancelTokenSource } from 'axios';
import { clearProceduresSearchCache } from 'services/searchProceduresServices';
import { noop } from 'utils/functionUtils';

interface State {
  currentConsultations: Array<ConsultationDto>,
  selectedConsultation?: ConsultationDto,
  changeConsultations: (consultations: Array<ConsultationDto>) => void,
  changeSelectedConsultation: (consultation?: ConsultationDto) => void,
  addConsultation: (consultation: ConsultationDto) => void,
  editConsultation: (consultation: ConsultationDto) => void,
  deleteConsultation: () => void,
  onOpenUpsertModal: () => void,
  onCloseUpsertModal: () => void,
  setConsultationId: () => void,
  consultationFormOpen: boolean,
  consultationId?: number,
  modifyConsultationsBillStatus: (consultationIds: Set<number>, billStatus: BillStatus) => void,
  setCacheSearchId: (searchId?: string) => void,
  searchId?: string,
  cancelTokenSource: CancelTokenSource
}

const cancelTokenSource = axios.CancelToken.source();

interface Props {
  children: JSX.Element | Array<JSX.Element>
}

export const ConsultationsContext = React.createContext<State>({
  currentConsultations: [],
  changeConsultations: () => {},
  changeSelectedConsultation: () => {},
  addConsultation: () => {},
  editConsultation: () => {},
  onOpenUpsertModal: () => {},
  onCloseUpsertModal: () => {},
  setConsultationId: () => {},
  deleteConsultation: () => {},
  modifyConsultationsBillStatus: () => {},
  setCacheSearchId: () => {},
  consultationFormOpen: false,
  consultationId: undefined,
  searchId: undefined,
  cancelTokenSource: cancelTokenSource
});

export class ConsultationsProvider extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    this.state = {
      currentConsultations: [],
      selectedConsultation: undefined,
      changeConsultations: this.changeConsultations,
      changeSelectedConsultation: this.changeSelectedConsultation,
      addConsultation: this.addConsultation,
      editConsultation: this.editConsultation,
      deleteConsultation: this.deleteConsultation,
      onOpenUpsertModal: this.onOpenUpsertConsultationModal,
      onCloseUpsertModal: this.onCloseUpsertConsultationModal,
      setConsultationId: this.setConsultationId,
      modifyConsultationsBillStatus: this.modifyConsultationsBillStatus,
      setCacheSearchId: this.setCacheSearchId,
      consultationFormOpen: false,
      consultationId: undefined,
      searchId: undefined,
      cancelTokenSource
    }
  }

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operations canceled by user');
  }

  changeSelectedConsultation = (consultation?: ConsultationDto) => {
    this.setState({selectedConsultation: consultation});
  };

  changeConsultations = (consultations: Array<ConsultationDto>) => {
    this.setState({
      currentConsultations: consultations,
      selectedConsultation: consultations.length > 0 ? consultations[0] : undefined
    });
  };

  onOpenUpsertConsultationModal = () => {
    this.setState(() => ({
      consultationFormOpen: true
    }));
  };

  onCloseUpsertConsultationModal = () => {
    const { searchId, cancelTokenSource } = this.state;

    if (searchId) {
      clearProceduresSearchCache(searchId, cancelTokenSource)
          .then(response => response)
          .catch(noop);
    }

    this.setState(() => ({
      searchId: undefined,
      consultationFormOpen: false,
      consultationId: undefined
    }));
  };

  setConsultationId = () => {

    const {selectedConsultation} = this.state;

    this.setState({
      consultationId: selectedConsultation ? selectedConsultation.id : undefined
    });
    this.onOpenUpsertConsultationModal();
  };

  addConsultation = (consultation: ConsultationDto) => {
    this.setState(({currentConsultations}: State) => ({
      currentConsultations: [consultation, ...currentConsultations],
      selectedConsultation: consultation
    }))
  };

  editConsultation = (consultation: ConsultationDto) => {

    const {currentConsultations} = this.state;
    const foundConsultationIndex = currentConsultations
        .findIndex(currentConsultation => currentConsultation.id == consultation.id);

    if (foundConsultationIndex != null) {
      currentConsultations[foundConsultationIndex] = consultation;

      this.setState({
        currentConsultations: currentConsultations,
        selectedConsultation: currentConsultations[foundConsultationIndex]
      })
    }
  };

  deleteConsultation = () => {

    const {currentConsultations, selectedConsultation} = this.state;

    if (selectedConsultation) {
      const foundConsultationIndex = currentConsultations
          .findIndex(currentConsultation => currentConsultation.id == selectedConsultation.id);

      if (foundConsultationIndex != null) {
        currentConsultations.splice(foundConsultationIndex, 1);

        this.setState({
          currentConsultations: currentConsultations,
          selectedConsultation: currentConsultations.length > 0 ? currentConsultations[0] : undefined
        })
      }
    }
  };

  modifyConsultationsBillStatus = (consultationIds: Set<number>, billStatus: BillStatus) => {

    const {currentConsultations} = this.state;

    if (consultationIds.size > 0) {
      const modifiedConsultations = Array.from(currentConsultations);

      modifiedConsultations.forEach(consultation => {
        if (consultationIds.has(consultation.id)) {
          consultation.billStatus = billStatus;
        }
      });

      this.setState({
        currentConsultations: modifiedConsultations
      });
    }
  };

  setCacheSearchId = (searchId?: string) => {
    this.setState({
      searchId
    });
  };

  render() {
    return (
        <ConsultationsContext.Provider value={this.state}>
          {this.props.children}
        </ConsultationsContext.Provider>
    );
  }
}

export const ConsultationsConsumer = ConsultationsContext.Consumer;

export interface ConsultationsConsumerRenderProps extends State {
}

export const withConsultationsContext = (Component: typeof React.Component | React.FunctionComponent<any>) => {
  return (props: any) => (
      <ConsultationsConsumer>
        {({...consultationProps}: ConsultationsConsumerRenderProps) => {
          return <Component {...props} {...consultationProps} />
        }}
      </ConsultationsConsumer>
  );
};
